import {create} from 'zustand';

// Mirrors Sources' upload queue (useSourcesUploadQueueStore.js) but simplified for Collection
// Material: file-only (no URL/paste web imports, no folder tree — CMs attach directly to a
// Collection Task), and matches server-hydrated items by `materialId` (the Collection Material
// row id) rather than a canonical DataItem id, since `createCollectionMaterialFromFile` creates
// the CM row synchronously in the same request that accepts the upload.
const CM_UPLOAD_CONCURRENCY = 1;
const SESSION_FILE_REGISTRY = new Map();

function createQueueItemId() {
    return `cm-upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function registerQueuedFile(file) {
    const key = `cm-file-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    SESSION_FILE_REGISTRY.set(key, file);
    return key;
}

export function getQueuedFile(fileKey) {
    return SESSION_FILE_REGISTRY.get(fileKey) || null;
}

export function removeQueuedFile(fileKey) {
    SESSION_FILE_REGISTRY.delete(fileKey);
}

function nowIso() {
    return new Date().toISOString();
}

function createQueueItem(file, options = {}, classification = {}, perFileTitle = null) {
    const fileKey = registerQueuedFile(file);
    return {
        id: createQueueItemId(),
        fileKey,
        name: file?.name || 'Untitled',
        size: Number(file?.size || 0),
        mimeType: file?.type || null,
        collectionTaskId: options.collectionTaskId || null,
        // Per-file title wins; fall back to shared options.title; final fallback is null
        // (backend uses the filename when title is absent).
        title: perFileTitle ?? options.title ?? null,
        collectionMethod: options.collectionMethod || 'OSINT',
        classificationLevel: classification.level || options.classificationLevel || null,
        distribution: Array.isArray(classification.distribution)
            ? classification.distribution
            : (Array.isArray(options.distribution) ? options.distribution : []),
        compartment: Array.isArray(classification.compartment)
            ? classification.compartment
            : (Array.isArray(options.compartment) ? options.compartment : []),
        status: 'queued',
        progress: 0,
        error: null,
        materialId: null,
        canonicalItemId: null,
        existingReferenceId: null,
        stage: null,
        progressPercent: null,
        startedAt: null,
        finishedAt: null,
        createdAt: nowIso(),
    };
}

const useCollectionMaterialUploadQueueStore = create(
    (set, get) => ({
        items: [],
        paused: false,
        concurrency: CM_UPLOAD_CONCURRENCY,
            enqueueUploads: (files, options = {}) => {
                const entries = Array.isArray(files) ? files : [];
                // `options.classifications[i]` (optional) carries per-file
                // {level, distribution, compartment} — used by the dialog's per-file classification
                // table. Falls back to the shared options.classificationLevel/distribution/
                // compartment for every file when omitted (bulk-only callers, e.g. drag&drop).
                const classifications = Array.isArray(options.classifications) ? options.classifications : [];
                // `options.titles[i]` (optional) carries per-file title strings set in the
                // dialog's Title column. Falls back to shared options.title when omitted.
                const titles = Array.isArray(options.titles) ? options.titles : [];
                const nextItems = entries.map((file, i) => createQueueItem(
                    file,
                    options,
                    classifications[i] || {},
                    titles.length ? (titles[i] ?? null) : null,
                ));
                set((state) => ({items: [...state.items, ...nextItems]}));
                return nextItems.map((item) => item.id);
            },
        /**
         * Hydrate queue state from GET /api/collection/materials/upload/queue.
         *
         * Rules (mirrors useSourcesUploadQueueStore.hydrateFromServer):
         *  1. Local items whose materialId matches a server item → update status/stage/
         *     progressPercent from the server (server wins; skip terminal items).
         *  2. Server items not yet in the local queue (new tab / new session / page refresh)
         *     → inject as synthetic queue items so the user sees their in-progress uploads.
         */
        hydrateFromServer: (serverItems) => {
            if (!serverItems || !serverItems.length) {
                return;
            }

            const TERMINAL = new Set(['success', 'cancelled', 'duplicate']);

            const deriveLocalStatus = (serverStatus) => {
                if (serverStatus === 'processed') return 'success';
                if (serverStatus === 'failed') return 'failed';
                return 'processing';
            };

            set((state) => {
                const existingByMaterialId = new Map(
                    state.items
                        .filter((i) => i.materialId)
                        .map((i) => [String(i.materialId), i]),
                );

                const updated = state.items.map((item) => {
                    if (!item.materialId) return item;
                    const server = serverItems.find((s) => String(s.id) === String(item.materialId));
                    if (!server) return item;
                    if (TERMINAL.has(item.status)) return item;
                    return {
                        ...item,
                        status: deriveLocalStatus(server.uploadStatus),
                        stage: server.uploadStage || null,
                        progressPercent: server.uploadProgressPercent ?? null,
                    };
                });

                const injected = serverItems
                    .filter((s) => !existingByMaterialId.has(String(s.id)))
                    .map((s) => ({
                        id: `server-${s.id}-${Math.random().toString(36).slice(2, 8)}`,
                        fileKey: null,
                        name: s.dataItemName || s.title || 'Untitled',
                        size: 0,
                        mimeType: s.mimeType || null,
                        collectionTaskId: s.collectionTaskId || null,
                        title: s.title || null,
                        collectionMethod: s.collectionMethod || 'OSINT',
                        classificationLevel: null,
                        distribution: [],
                        compartment: [],
                        status: deriveLocalStatus(s.uploadStatus),
                        progress: s.uploadProgressPercent != null ? s.uploadProgressPercent : 0,
                        stage: s.uploadStage || null,
                        progressPercent: s.uploadProgressPercent ?? null,
                        error: null,
                        materialId: String(s.id),
                        canonicalItemId: s.dataItemId != null ? String(s.dataItemId) : null,
                        existingReferenceId: null,
                        startedAt: null,
                        finishedAt: null,
                        createdAt: s.createdAt || nowIso(),
                    }));

                return {items: [...updated, ...injected]};
            });
        },
        markUploading: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, status: 'uploading', startedAt: item.startedAt || nowIso(), error: null}
                        : item
                )),
            }));
        },
        markProgress: (itemId, progress) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, progress: Math.max(item.progress || 0, progress)}
                        : item
                )),
            }));
        },
        // The XHR transfer completing means the CM row now exists (createFromFile is
        // synchronous) but the file pipeline (extraction/embedding/etc.) almost always keeps
        // running async — set 'processing' so the polling loop tracks it to completion, mirroring
        // Sources' non-instant path.
        markSuccess: (itemId, payload = {}) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'processing',
                            progress: 100,
                            progressPercent: 0,   // start at 0%; 5s poll will update from server
                            error: null,
                            materialId: payload.materialId || null,
                            canonicalItemId: payload.canonicalItemId || null,
                        }
                        : item
                )),
            }));
        },
        markFailed: (itemId, message) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, status: 'failed', error: message || 'Upload failed', finishedAt: nowIso()}
                        : item
                )),
            }));
        },
        markDuplicate: (itemId, message, existingReferenceId = null) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'duplicate',
                            progress: 100,
                            error: message || 'This file is already in My files.',
                            existingReferenceId: existingReferenceId ?? null,
                            finishedAt: nowIso(),
                        }
                        : item
                )),
            }));
        },
        markCancelled: (itemId, message = 'Canceled') => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, status: 'cancelled', error: message, finishedAt: nowIso()}
                        : item
                )),
            }));
        },
        markPaused: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, status: 'paused', error: 'Paused'}
                        : item
                )),
            }));
        },
        retryItem: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, status: 'queued', progress: 0, error: null, finishedAt: null}
                        : item
                )),
            }));
        },
        pauseAll: () => set({paused: true}),
        resumeAll: () => {
            set((state) => ({
                paused: false,
                items: state.items.map((item) => (
                    item.status === 'paused' ? {...item, status: 'queued', error: null} : item
                )),
            }));
        },
        setPaused: (paused) => set({paused}),
        clearCompleted: () => {
            const items = get().items;
            items.forEach((item) => {
                if (item.status === 'success' || item.status === 'cancelled' || item.status === 'duplicate') {
                    removeQueuedFile(item.fileKey);
                }
            });
            set((state) => ({
                items: state.items.filter((item) => (
                    item.status !== 'success' && item.status !== 'cancelled' && item.status !== 'duplicate'
                )),
            }));
        },
        clearAll: () => {
            const items = get().items;
            items.forEach((item) => removeQueuedFile(item.fileKey));
            set({items: []});
        },
        removeItem: (itemId) => {
            const item = get().items.find((candidate) => candidate.id === itemId);
            if (item) {
                removeQueuedFile(item.fileKey);
            }
            set((state) => ({items: state.items.filter((candidate) => candidate.id !== itemId)}));
        },
    })
);

export default useCollectionMaterialUploadQueueStore;
